/**
 * DetailDrawer — slide-over panel to show entity details.
 */
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export interface DrawerField {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  tone?: "default" | "destructive" | "outline" | "secondary";
}

export interface DetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle?: string;
  badge?: { label: string; tone?: "default" | "destructive" | "outline" | "secondary" };
  fields: DrawerField[];
  actions?: React.ReactNode;
}

export function DetailDrawer({ open, onOpenChange, title, subtitle, badge, fields, actions }: DetailDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="space-y-1">
          <div className="flex items-center gap-2">
            {badge && <Badge variant={badge.tone}>{badge.label}</Badge>}
            <SheetTitle>{title}</SheetTitle>
          </div>
          {subtitle && <SheetDescription>{subtitle}</SheetDescription>}
        </SheetHeader>
        <div className="mt-6 space-y-3">
          {fields.map((f, i) => (
            <div key={i} className="grid grid-cols-3 gap-3 border-b border-white/5 pb-2">
              <dt className="text-xs uppercase text-muted-foreground pt-1">{f.label}</dt>
              <dd className={`col-span-2 text-sm ${f.mono ? "font-mono" : ""}`}>
                {f.tone ? <Badge variant={f.tone}>{f.value}</Badge> : f.value}
              </dd>
            </div>
          ))}
          {actions && <div className="pt-4 flex items-center gap-2">{actions}</div>}
        </div>
      </SheetContent>
    </Sheet>
  );
}